import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Package, Truck, XCircle } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';

export function OCDetallePage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [recOpen, setRecOpen] = useState(false);

  const { data: oc } = useQuery<any>({ queryKey: ['oc', id], queryFn: () => api(`/ordenes-compra/${id}`) });
  const { data: recepciones } = useQuery<any[]>({
    queryKey: ['oc-recepciones', id],
    queryFn: () => api(`/ordenes-compra/${id}/recepciones`),
  });

  if (!oc) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  const puedeRecibir = oc.estado === 'emitida' || oc.estado === 'parcialmente_recibida';

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/ordenes-compra" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a OC
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <Package size={20} />
            </div>
            <div>
              <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700 dark:text-brand-300">{oc.folio}</p>
              <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">{oc.proveedor_nombre}</h1>
              <p className="text-[12px] text-ink-mute">{fmt.date(oc.fecha)} · {oc.estado.replace('_', ' ')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {puedeRecibir && (
              <Button onClick={() => setRecOpen(true)}>
                <Truck size={14} /> Registrar recepción
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat label="Total" value={fmt.money(oc.total)} />
        <Stat label="Subtotal" value={fmt.money(oc.subtotal)} />
        <Stat label="IVA" value={fmt.money(oc.iva)} />
        <Stat label="Entrega" value={oc.fecha_entrega_pactada ? fmt.date(oc.fecha_entrega_pactada) : 'No pactada'} />
      </div>

      <Card>
        <CardHeader title="Líneas de la orden" />
        <CardBody className="p-0">
          <table className="min-w-full text-[13px]">
            <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
              <tr>
                <th className="px-5 py-3">#</th>
                <th className="px-5 py-3">Descripción</th>
                <th className="px-5 py-3 text-right">Ordenada</th>
                <th className="px-5 py-3 text-right">Recibida</th>
                <th className="px-5 py-3 text-right">Pendiente</th>
                <th className="px-5 py-3 text-right">P.U.</th>
                <th className="px-5 py-3 text-right">Total</th>
                <th className="px-5 py-3 text-center">AF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(oc.lineas ?? []).map((l: any) => (
                <tr key={l.id}>
                  <td className="num px-5 py-3">{l.secuencia}</td>
                  <td className="px-5 py-3">
                    <div>{l.descripcion}</div>
                    {l.articulo_clave && <div className="text-[11px] text-ink-mute">{l.articulo_clave}</div>}
                  </td>
                  <td className="num px-5 py-3 text-right">{fmt.num(l.cantidad_ordenada)} {l.unidad ?? ''}</td>
                  <td className="num px-5 py-3 text-right text-emerald-700">{fmt.num(l.cantidad_recibida)}</td>
                  <td className="num px-5 py-3 text-right font-semibold">{fmt.num(l.pendiente)}</td>
                  <td className="num px-5 py-3 text-right">{fmt.money(l.precio_unitario)}</td>
                  <td className="num px-5 py-3 text-right font-semibold">{fmt.money(l.total)}</td>
                  <td className="px-5 py-3 text-center">{l.es_activo_fijo ? '✓' : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Recepciones" subtitle={`${(recepciones ?? []).length} recepciones registradas`} />
        <CardBody className="p-0">
          {!recepciones || recepciones.length === 0 ? (
            <div className="p-6 text-center text-[13px] text-ink-mute">Sin recepciones aún.</div>
          ) : (
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                <tr>
                  <th className="px-5 py-3">Folio</th>
                  <th className="px-5 py-3">Fecha</th>
                  <th className="px-5 py-3">Factura</th>
                  <th className="px-5 py-3 text-right">Importe</th>
                  <th className="px-5 py-3">Vínculos</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {recepciones.map((r) => (
                  <tr key={r.id}>
                    <td className="num px-5 py-3 font-semibold">{r.folio}</td>
                    <td className="num px-5 py-3">{fmt.date(r.fecha)}</td>
                    <td className="px-5 py-3 text-ink-mute">{r.factura_folio ?? '—'}</td>
                    <td className="num px-5 py-3 text-right font-semibold">{fmt.money(r.total)}</td>
                    <td className="px-5 py-3 text-[11px]">
                      {r.cxp_id && <span className="mr-2 rounded bg-amber-50 px-1.5 py-0.5 text-amber-800">CxP</span>}
                      {r.asiento_id && <span className="mr-2 rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">Póliza</span>}
                      {r.movimiento_inventario_id && <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700">Almacén</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      <RecepcionModal ocID={id} oc={oc}
        open={recOpen}
        onClose={() => setRecOpen(false)}
        onDone={() => {
          setRecOpen(false);
          qc.invalidateQueries({ queryKey: ['oc', id] });
          qc.invalidateQueries({ queryKey: ['oc-recepciones', id] });
        }}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-2 text-[20px] font-semibold text-ink">{value}</div>
    </div>
  );
}

function RecepcionModal({ ocID, oc, open, onClose, onDone }: {
  ocID: string; oc: any; open: boolean; onClose: () => void; onDone: () => void;
}) {
  const pendientes = (oc?.lineas ?? []).filter((l: any) => l.pendiente > 0);
  const [cantidades, setCantidades] = useState<Record<string, number>>({});
  const [factura, setFactura] = useState('');
  const [facturaUUID, setFacturaUUID] = useState('');
  const [almacenID, setAlmacenID] = useState('');
  const [iva, setIva] = useState(0);
  const [crearCxP, setCrearCxP] = useState(true);
  const [fechaVenc, setFechaVenc] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  const { data: almacenes } = useQuery({
    queryKey: ['almacenes'], enabled: open,
    queryFn: () => api<any[]>('/almacenes'),
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const lineas = Object.entries(cantidades)
      .filter(([_, q]) => q > 0)
      .map(([oc_linea_id, cantidad_recibida]) => ({ oc_linea_id, cantidad_recibida }));
    if (lineas.length === 0) { setErr('Captura al menos una cantidad'); return; }
    setSubm(true); setErr(null);
    try {
      await api(`/ordenes-compra/${ocID}/recepcionar`, {
        method: 'POST',
        body: {
          factura_folio: factura || undefined,
          factura_uuid: facturaUUID || undefined,
          almacen_id: almacenID || undefined,
          iva, crear_cxp: crearCxP,
          fecha_vencimiento_cxp: fechaVenc || undefined,
          lineas,
        },
      });
      onDone();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally { setSubm(false); }
  }

  if (!open) return null;

  const subtotal = Object.entries(cantidades).reduce((acc, [id, qty]) => {
    const l = pendientes.find((x: any) => x.id === id);
    return acc + (l ? qty * l.precio_unitario : 0);
  }, 0);
  const total = subtotal + iva;

  return (
    <Modal open={open} onClose={onClose} title="Registrar recepción"
      subtitle="La recepción generará entrada al almacén (o alta de bien patrimonial), CxP y póliza de devengado"
      size="xl"
      footer={<>
        <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
        <Button type="submit" form="rec-form" loading={subm}>Recibir · {fmt.money(total)}</Button>
      </>}>
      <form id="rec-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{err}</div>}

        <div className="grid grid-cols-2 gap-3">
          <Input name="factura" label="Folio factura (CFDI)" value={factura} onChange={(e) => setFactura(e.target.value)} />
          <Input name="factura_uuid" label="UUID CFDI" value={facturaUUID} onChange={(e) => setFacturaUUID(e.target.value)} />
          <div className="col-span-2">
            <label className="text-[13px] font-medium text-ink">Almacén destino (para artículos consumibles)</label>
            <select value={almacenID} onChange={(e) => setAlmacenID(e.target.value)}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px]">
              <option value="">(sin almacén — todo es activo fijo)</option>
              {(almacenes ?? []).map((a: any) => (
                <option key={a.id} value={a.id}>{a.clave} — {a.nombre}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">Cantidades a recibir</h4>
          <div className="overflow-hidden rounded-md border border-border">
            <table className="min-w-full text-[13px]">
              <thead className="bg-muted/60 text-left text-[10px] font-semibold uppercase text-ink-mute">
                <tr>
                  <th className="px-3 py-2">Descripción</th>
                  <th className="px-3 py-2 text-right">Pendiente</th>
                  <th className="px-3 py-2 text-right">A recibir</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-center">AF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {pendientes.length === 0 && (
                  <tr><td colSpan={5} className="p-6 text-center text-[12px] text-ink-mute">No hay líneas pendientes de recibir.</td></tr>
                )}
                {pendientes.map((l: any) => {
                  const q = cantidades[l.id] ?? 0;
                  return (
                    <tr key={l.id}>
                      <td className="px-3 py-2">
                        <div>{l.descripcion}</div>
                        {l.articulo_clave && <div className="text-[11px] text-ink-mute">{l.articulo_clave}</div>}
                      </td>
                      <td className="num px-3 py-2 text-right">{fmt.num(l.pendiente)}</td>
                      <td className="px-3 py-2 text-right">
                        <input type="number" step="0.001" min="0" max={l.pendiente}
                          value={q}
                          onChange={(e) => setCantidades({ ...cantidades, [l.id]: parseFloat(e.target.value) || 0 })}
                          className="num h-8 w-24 rounded border border-border bg-surface px-2 text-right" />
                      </td>
                      <td className="num px-3 py-2 text-right">{fmt.money(q * l.precio_unitario)}</td>
                      <td className="px-3 py-2 text-center">{l.es_activo_fijo ? '✓' : ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Input name="iva" label="IVA" type="number" step="0.01" value={iva} onChange={(e) => setIva(parseFloat(e.target.value) || 0)} />
          <Input name="venc" label="Vencimiento CxP" type="date" value={fechaVenc} onChange={(e) => setFechaVenc(e.target.value)} />
        </div>

        <label className="inline-flex items-center gap-2 text-[13px]">
          <input type="checkbox" checked={crearCxP} onChange={(e) => setCrearCxP(e.target.checked)}
            className="h-3.5 w-3.5 accent-brand-500" />
          Crear cuenta por pagar al proveedor
        </label>
      </form>
    </Modal>
  );
}

// @ts-expect-error
const _ = XCircle;
